import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

const BACKEND = 'https://alphabioai-backend.onrender.com';

const T = {
  bg:        '#060d1e',
  surface:   '#0d1a2d',
  surfaceHi: '#132238',
  border:    '#1e3a5f',
  borderHi:  '#2d5a8e',
  text:      '#f0f6ff',
  textSub:   '#8ba5c8',
  textMute:  '#4a6a8a',
  emerald:   '#10b981',
  emeraldDim:'rgba(16,185,129,0.10)',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
  gold:      '#fbbf24',
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.ceil((new Date(dateStr) - today) / (1000*60*60*24));
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmt$ = (n) => {
  if (!n || n === 0) return '—';
  if (n >= 1_000_000) return `$${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n/1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

/* ─── SVG Icons ─── */
const AlertIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="9" x2="12" y2="13" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="12" cy="17" r="1" fill={color}/>
  </svg>
);

const TrendDownIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 18 23 18 23 12" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrendUpIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 6 23 6 23 12" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalendarIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke={color} strokeWidth="1.8" fill="none"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth="1.8"/>
    <circle cx="8" cy="15" r="1" fill={color}/>
    <circle cx="12" cy="15" r="1" fill={color}/>
  </svg>
);

const ZapIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PillIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M10.5 20H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M16 19h6M19 16v6" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="2" y1="10" x2="22" y2="10" stroke={color} strokeWidth="1.8"/>
  </svg>
);

const TargetIcon = ({ color, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.8" fill="none"/>
    <circle cx="12" cy="12" r="6" stroke={color} strokeWidth="1.8" fill="none"/>
    <circle cx="12" cy="12" r="2" fill={color}/>
  </svg>
);

const ArrowRightIcon = ({ color = '#4a6a8a', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <line x1="5" y1="12" x2="19" y2="12" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
    <polyline points="12 5 19 12 12 19" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ─── Smart Attention Banner ─── */
function AttentionBanner({ upcomingPDUFA, recentTrades, collisions, newDrugs, onTabChange, isMobile }) {
  const items = [];

  const urgentPDUFA = upcomingPDUFA.filter(e => {
    const d = daysUntil(e.decision_date);
    return d !== null && d >= 0 && d <= 7;
  });
  if (urgentPDUFA.length > 0) {
    const top = urgentPDUFA[0];
    const days = daysUntil(top.decision_date);
    items.push({
      IconComponent: AlertIcon,
      color: T.red,
      bg: 'rgba(239,68,68,0.10)',
      border: 'rgba(239,68,68,0.28)',
      label: 'URGENT FDA DECISION',
      title: `${top.drug_name} — ${days === 0 ? 'Decision TODAY' : days === 1 ? 'Decision tomorrow' : `Decision in ${days} days`}`,
      sub: `${top.company && top.company !== 'Unknown' ? top.company : ''}${top.indication ? ` · ${top.indication.substring(0, 55)}` : ''}`,
      tab: 'calendar',
    });
  } else if (upcomingPDUFA.length > 0) {
    const top = upcomingPDUFA[0];
    const days = daysUntil(top.decision_date);
    items.push({
      IconComponent: CalendarIcon,
      color: T.amber,
      bg: 'rgba(245,158,11,0.10)',
      border: 'rgba(245,158,11,0.28)',
      label: 'UPCOMING FDA DECISION',
      title: `${top.drug_name} — ${days}d away`,
      sub: `${top.company && top.company !== 'Unknown' ? top.company : ''}${top.indication ? ` · ${top.indication.substring(0, 55)}` : ''}`,
      tab: 'calendar',
    });
  }

  const bigTrades = [...recentTrades].sort((a, b) => (b.total_value || 0) - (a.total_value || 0));
  if (bigTrades.length > 0) {
    const top = bigTrades[0];
    const isBuy = top.trade_type === 'BUY';
    items.push({
      IconComponent: isBuy ? TrendUpIcon : TrendDownIcon,
      color: isBuy ? T.green : T.red,
      bg: isBuy ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
      border: isBuy ? 'rgba(34,197,94,0.28)' : 'rgba(239,68,68,0.28)',
      label: `INSIDER ${isBuy ? 'BUY' : 'SELL'}`,
      title: `${top.ticker} — ${fmt$(top.total_value)} ${isBuy ? 'purchased' : 'sold'} by ${top.insider_name || 'insider'}`,
      sub: `${top.insider_title?.substring(0, 50) || ''} · ${fmtDate(top.trade_date)}`,
      tab: 'insider',
    });
  }

  if (collisions.length > 0) {
    const c = collisions[0];
    items.push({
      IconComponent: ZapIcon,
      color: T.gold,
      bg: 'rgba(251,191,36,0.10)',
      border: 'rgba(251,191,36,0.28)',
      label: 'EARNINGS + FDA COLLISION',
      title: `${c.ticker} — Earnings & FDA decision same week`,
      sub: `Earnings ${fmtDate(c.earnings_date)} · FDA: ${c.fda_drug?.substring(0, 40)} on ${fmtDate(c.fda_date)}`,
      tab: 'earnings',
    });
  } else {
    const highRisk = newDrugs.filter(d => d.risk_level === 'High');
    if (highRisk.length > 0) {
      const top = highRisk[0];
      items.push({
        IconComponent: PillIcon,
        color: T.red,
        bg: 'rgba(239,68,68,0.10)',
        border: 'rgba(239,68,68,0.28)',
        label: 'HIGH RISK DRUG ADDED',
        title: `${top.drug_name} flagged as high risk`,
        sub: `${top.manufacturer?.substring(0, 50) || ''} · Added this week`,
        tab: 'fda',
      });
    } else if (newDrugs.length > 0) {
      items.push({
        IconComponent: PillIcon,
        color: T.blue,
        bg: 'rgba(59,130,246,0.10)',
        border: 'rgba(59,130,246,0.28)',
        label: 'NEW DRUGS ADDED',
        title: `${newDrugs.length} new FDA drug${newDrugs.length !== 1 ? 's' : ''} added this week`,
        sub: `Latest: ${newDrugs[0].drug_name} · ${newDrugs[0].manufacturer?.substring(0, 40) || ''}`,
        tab: 'fda',
      });
    }
  }

  if (items.length === 0) return null;

  return (
    <div style={{ marginBottom: 28, backgroundColor: T.surface, border: `1px solid ${T.borderHi}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: isMobile ? '14px 18px' : '16px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 12, background: 'linear-gradient(135deg,rgba(59,130,246,0.06),rgba(239,68,68,0.04))' }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <TargetIcon color={T.red} size={16} />
        </div>
        <div>
          <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: T.text, letterSpacing: '-0.3px' }}>
            {items.length === 1 ? '1 Thing Needs Your Attention' : `${items.length} Things Need Your Attention`}
          </div>
          <div style={{ fontSize: 13, color: T.textMute, marginTop: 2 }}>Based on live data · Updated now</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.slice(0, 3).map((item, i) => {
          const { IconComponent } = item;
          return (
            <div key={i} onClick={() => onTabChange?.(item.tab)}
              style={{ padding: isMobile ? '14px 18px' : '18px 24px', borderBottom: i < items.length - 1 ? `1px solid ${T.border}` : 'none', display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16, cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = T.surfaceHi}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <div style={{ width: isMobile ? 36 : 42, height: isMobile ? 36 : 42, borderRadius: 10, backgroundColor: item.bg, border: `1px solid ${item.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconComponent color={item.color} size={isMobile ? 17 : 20} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: item.color, letterSpacing: '1px', marginBottom: 5 }}>{item.label}</div>
                <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: T.text, marginBottom: 4, lineHeight: 1.3 }}>{item.title}</div>
                {item.sub && <div style={{ fontSize: isMobile ? 12 : 13, color: T.textSub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.sub}</div>}
              </div>
              <ArrowRightIcon color={T.textMute} size={18} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Pro Brief Card ─── */
function ProBriefCard({ session, watchlist, isMobile }) {
  const [brief,     setBrief]     = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [decisions, setDecisions] = useState([]);
  const [trades,    setTrades]    = useState([]);
  const [error,     setError]     = useState(null);
  const [genTime,   setGenTime]   = useState(null);

  const fetchBrief = useCallback(async () => {
    if (!session?.user) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${BACKEND}/api/personalized-brief`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: session.user.id, email: session.user.email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate brief');
      setBrief(data.brief); setDecisions(data.relevant_decisions || []); setTrades(data.relevant_trades || []);
      setGenTime(new Date().toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' }));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [session]);

  useEffect(() => { fetchBrief(); }, [fetchBrief]);

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:3, height:24, backgroundColor: T.purple, borderRadius:2 }} />
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <h3 style={{ margin:0, fontSize: isMobile ? 17 : 20, fontWeight:800, color: T.text, letterSpacing:'-0.3px' }}>Your Watchlist Brief</h3>
              <span style={{ fontSize:10, fontWeight:800, color: T.purple, backgroundColor:'rgba(167,139,250,0.12)', border:'1px solid rgba(167,139,250,0.25)', padding:'2px 8px', borderRadius:100, letterSpacing:'0.5px' }}>PRO</span>
            </div>
            <div style={{ fontSize:13, color: T.textMute, marginTop:3 }}>
              AI-written brief based on your {watchlist.length} watched item{watchlist.length !== 1 ? 's' : ''}{genTime && <span> · Generated at {genTime}</span>}
            </div>
          </div>
        </div>
        <button onClick={fetchBrief} disabled={loading}
          style={{ backgroundColor:'transparent', border:`1px solid rgba(167,139,250,0.3)`, color: T.purple, padding:'6px 14px', borderRadius:7, cursor: loading ? 'default' : 'pointer', fontSize:13, fontWeight:600, transition:'all 0.15s', opacity: loading ? 0.6 : 1 }}
          onMouseEnter={e => { if (!loading) { e.currentTarget.style.borderColor = T.purple; e.currentTarget.style.background = 'rgba(167,139,250,0.08)'; }}}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(167,139,250,0.3)'; e.currentTarget.style.background = 'transparent'; }}>
          {loading ? 'Generating...' : 'Refresh'}
        </button>
      </div>

      {watchlist.length === 0 && !loading && (
        <div style={{ backgroundColor: T.surface, border:`1px solid rgba(167,139,250,0.2)`, borderLeft:`3px solid ${T.purple}`, borderRadius:12, padding:'20px 22px' }}>
          <div style={{ fontSize:15, color: T.textSub, marginBottom:6 }}>Your watchlist is empty.</div>
          <div style={{ fontSize:14, color: T.textMute }}>Star drugs and companies in the FDA Drugs or FDA Calendar tabs to get a personalized brief here.</div>
        </div>
      )}

      {loading && (
        <div style={{ backgroundColor: T.surface, border:`1px solid rgba(167,139,250,0.2)`, borderLeft:`3px solid ${T.purple}`, borderRadius:12, padding:'28px 22px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', backgroundColor: T.purple, animation:'pulse 1.4s ease-in-out infinite' }} />
            <span style={{ fontSize:14, color: T.textSub }}>Your AI analyst is reviewing your watchlist...</span>
          </div>
          <div style={{ width:'100%', height:'2px', backgroundColor: T.border, borderRadius:1, overflow:'hidden' }}>
            <div style={{ height:'100%', width:'40%', backgroundColor: T.purple, animation:'loadBar 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      )}

      {error && !loading && (
        <div style={{ backgroundColor: T.surface, border:`1px solid rgba(239,68,68,0.2)`, borderLeft:`3px solid ${T.red}`, borderRadius:12, padding:'16px 20px' }}>
          <div style={{ fontSize:14, color: T.red, marginBottom:6, fontWeight:600 }}>Could not generate brief</div>
          <div style={{ fontSize:13, color: T.textMute }}>{error}</div>
        </div>
      )}

      {brief && !loading && (
        <div>
          <div style={{ backgroundColor: T.surface, border:`1px solid rgba(167,139,250,0.2)`, borderLeft:`3px solid ${T.purple}`, borderRadius:12, padding: isMobile ? '18px 16px' : '22px 24px', marginBottom:12 }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
              <div style={{ width:34, height:34, borderRadius:9, background:'linear-gradient(135deg,rgba(167,139,250,0.3),rgba(139,92,246,0.2))', border:`1px solid rgba(167,139,250,0.25)`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke={T.purple} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, fontWeight:800, color: T.purple, letterSpacing:'1px', marginBottom:10, textTransform:'uppercase' }}>Your AI Analyst</div>
                <p style={{ margin:0, fontSize: isMobile ? 14 : 15, color:'#c8deff', lineHeight:1.8 }}>{brief}</p>
              </div>
            </div>
          </div>

          {decisions.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color: T.textMute, letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:8 }}>Watchlist FDA Decisions</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {decisions.map((d,i) => {
                  const urgent = d.days_until <= 7;
                  const color = urgent ? T.red : T.amber;
                  return (
                    <div key={i} style={{ backgroundColor: T.surfaceHi, border:`1px solid ${T.border}`, borderLeft:`3px solid ${color}`, borderRadius:8, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:14, fontWeight:700, color: T.text }}>{d.drug_name}</div>
                        <div style={{ fontSize:12, color: T.textSub, marginTop:2 }}>{d.company}{d.indication ? ` · ${d.indication.substring(0,50)}` : ''}</div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontSize:18, fontWeight:800, color, lineHeight:1 }}>{d.days_until === 0 ? 'TODAY' : d.days_until === 1 ? '1d' : `${d.days_until}d`}</div>
                        <div style={{ fontSize:11, color: T.textMute, marginTop:2 }}>{fmtDate(d.decision_date)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {trades.length > 0 && (
            <div>
              <div style={{ fontSize:12, fontWeight:700, color: T.textMute, letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:8 }}>Watchlist Insider Activity</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {trades.map((t,i) => {
                  const isBuy = t.trade_type === 'BUY';
                  const color = isBuy ? T.green : T.red;
                  return (
                    <div key={i} style={{ backgroundColor: T.surfaceHi, border:`1px solid ${T.border}`, borderLeft:`3px solid ${color}`, borderRadius:8, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:3 }}>
                          <span style={{ fontSize:14, fontWeight:800, color: T.emerald }}>{t.ticker}</span>
                          <span style={{ fontSize:11, fontWeight:700, padding:'1px 7px', borderRadius:100, background: isBuy ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color }}>{isBuy ? '▲ BUY' : '▼ SELL'}</span>
                        </div>
                        <div style={{ fontSize:12, color: T.textSub }}>{t.insider_name} · {t.insider_title?.substring(0,35)}</div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontSize:15, fontWeight:700, color }}>{t.total_value >= 1_000_000 ? `$${(t.total_value/1_000_000).toFixed(1)}M` : t.total_value >= 1_000 ? `$${(t.total_value/1_000).toFixed(0)}K` : `$${t.total_value}`}</div>
                        <div style={{ fontSize:11, color: T.textMute, marginTop:2 }}>{fmtDate(t.trade_date)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main component ─── */
export default function WeeklyBrief({ session, plan, onTabChange, watchlist = [], onUpgrade }) {
  const [loading, setLoading] = useState(true);
  const [newDrugs, setNewDrugs] = useState([]);
  const [newTrials, setNewTrials] = useState([]);
  const [recentTrades, setRecentTrades] = useState([]);
  const [upcomingPDUFA, setUpcomingPDUFA] = useState([]);
  const [collisions, setCollisions] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    fetchAll();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function fetchAll() {
    setLoading(true);
    const today   = new Date().toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 10*24*60*60*1000).toISOString().split('T')[0];
    const next14  = new Date(Date.now() + 90*24*60*60*1000).toISOString().split('T')[0];

    const [drugsRes, trialsRes, tradesRes, pdufaRes, earningsRes, fdaRes] = await Promise.all([
      supabase.from('fda_drugs').select('drug_name,manufacturer,risk_level,created_at').gte('created_at', weekAgo + 'T00:00:00').order('created_at', { ascending: false }).limit(10),
      supabase.from('clinical_trials').select('title,company,phase,status,created_at').gte('created_at', weekAgo + 'T00:00:00').order('created_at', { ascending: false }).limit(10),
      supabase.from('insider_trades').select('ticker,company_name,insider_name,insider_title,trade_type,total_value,trade_date,has_fda_event').gte('trade_date', weekAgo).order('trade_date', { ascending: false }).limit(10),
      supabase.from('fda_calendar').select('*').eq('status', 'pending').gte('decision_date', today).lte('decision_date', next14).order('decision_date'),
      supabase.from('earnings_calendar').select('*').gte('earnings_date', today).order('earnings_date'),
      supabase.from('fda_calendar').select('*').eq('status', 'pending').gte('decision_date', today).order('decision_date'),
    ]);

    setNewDrugs(drugsRes.data || []);
    setNewTrials(trialsRes.data || []);
    setRecentTrades(tradesRes.data || []);
    setUpcomingPDUFA(pdufaRes.data || []);

    const earningsList = earningsRes.data || [];
    const fdaList      = fdaRes.data || [];
    const found = [];
    for (const e of earningsList) {
      const eDate = new Date(e.earnings_date + 'T00:00:00');
      const weekStart = new Date(eDate); weekStart.setDate(eDate.getDate() - eDate.getDay());
      const weekEnd   = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
      const eTicker   = (e.ticker || '').toUpperCase();
      const eCompany  = (e.company_name || '').toLowerCase();
      for (const f of fdaList) {
        if (!f.decision_date) continue;
        const fDate    = new Date(f.decision_date + 'T00:00:00');
        const fCompany = (f.company || '').toLowerCase();
        const sameWeek = fDate >= weekStart && fDate <= weekEnd;
        const match    = fCompany.includes(eTicker.toLowerCase()) || eCompany.split(' ').some(w => w.length > 3 && fCompany.includes(w));
        if (sameWeek && match) found.push({ ticker: eTicker, company: e.company_name, earnings_date: e.earnings_date, fda_drug: f.drug_name, fda_date: f.decision_date });
      }
    }
    setCollisions(found);
    setLoading(false);
  }

  if (loading) return (
    <div style={{ textAlign:'center', padding:'80px', color: T.textMute }}>
      <div style={{ fontSize:'15px', marginBottom:'12px' }}>Loading this week's intelligence...</div>
      <div style={{ width:'80px', height:'2px', backgroundColor: T.border, borderRadius:'1px', overflow:'hidden', margin:'0 auto' }}>
        <div style={{ height:'100%', width:'50%', backgroundColor: T.emerald, animation:'loadBar 1.4s ease-in-out infinite' }} />
      </div>
    </div>
  );

  const hasAnything = newDrugs.length > 0 || newTrials.length > 0 || recentTrades.length > 0 || upcomingPDUFA.length > 0;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>This Week in Biotech</h2>
        <p style={{ color: T.textSub, fontSize: 15, margin: 0 }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} · Auto-refreshes with live data
        </p>
      </div>

      <AttentionBanner upcomingPDUFA={upcomingPDUFA} recentTrades={recentTrades} collisions={collisions} newDrugs={newDrugs} onTabChange={onTabChange} isMobile={isMobile} />

      {plan === 'pro' && <ProBriefCard session={session} watchlist={watchlist} isMobile={isMobile} />}

      {plan !== 'pro' && (
        <div style={{ marginBottom:28, backgroundColor: T.surface, border:`1px solid rgba(167,139,250,0.2)`, borderLeft:`3px solid ${T.purple}`, borderRadius:12, padding: isMobile ? '16px' : '18px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
              <span style={{ fontSize:14, fontWeight:700, color: T.text }}>Your Watchlist Brief</span>
              <span style={{ fontSize:10, fontWeight:800, color: T.purple, backgroundColor:'rgba(167,139,250,0.12)', border:'1px solid rgba(167,139,250,0.25)', padding:'2px 8px', borderRadius:100 }}>PRO</span>
            </div>
            <div style={{ fontSize:13, color: T.textMute }}>Get an AI-written brief specific to your watchlist every time you open this tab.</div>
          </div>
          <button onClick={onUpgrade} style={{ fontSize:13, fontWeight:600, color: T.purple, backgroundColor:'rgba(167,139,250,0.08)', border:`1px solid rgba(167,139,250,0.2)`, padding:'8px 16px', borderRadius:8, whiteSpace:'nowrap', cursor:'pointer', fontFamily:'inherit' }}>Upgrade to Pro →</button>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap:10, marginBottom:28 }}>
        {[
          { label:'New Drugs This Week',    value: newDrugs.length,      color: T.blue,   onClick: () => onTabChange?.('fda') },
          { label:'New Trials This Week',   value: newTrials.length,     color: T.purple, onClick: () => onTabChange?.('trials') },
          { label:'Insider Trades',         value: recentTrades.length,  color: T.gold,   onClick: () => onTabChange?.('insider') },
          { label:'FDA Decisions (90 days)', value: upcomingPDUFA.length, color: T.red, onClick: () => onTabChange?.('calendar') },
        ].map((s,i) => (
          <div key={i} onClick={s.onClick} style={{ backgroundColor: T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding: isMobile ? '14px 16px' : '18px 22px', cursor:'pointer', transition:'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.borderHi}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
            <div style={{ fontSize:11, color: T.textMute, fontWeight:700, marginBottom:8, textTransform:'uppercase', letterSpacing:'0.8px' }}>{s.label}</div>
            <div style={{ fontSize: isMobile ? 26 : 32, fontWeight:800, color: s.color, lineHeight:1 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {upcomingPDUFA.length > 0 && (
        <Section title="Upcoming FDA Decisions" subtitle={`Next 14 days — ${upcomingPDUFA.length} decision${upcomingPDUFA.length !== 1 ? 's' : ''}`} accent={T.red} isMobile={isMobile}>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {upcomingPDUFA.map((entry, i) => {
              const days   = daysUntil(entry.decision_date);
              const urgent = days !== null && days <= 7;
              return (
                <div key={i} style={{ backgroundColor: urgent ? 'rgba(239,68,68,0.05)' : T.surface, border:`1px solid ${urgent ? 'rgba(239,68,68,0.3)' : T.border}`, borderLeft:`3px solid ${urgent ? T.red : T.amber}`, borderRadius:10, padding:'16px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, color: T.text, fontSize:15, marginBottom:4 }}>{entry.drug_name}</div>
                    <div style={{ fontSize:13, color: T.textSub }}>{entry.company && entry.company !== 'Unknown' ? entry.company : ''}{entry.indication ? ` · ${entry.indication.substring(0,60)}` : ''}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize: isMobile ? 20 : 24, fontWeight:800, color: urgent ? T.red : T.amber, lineHeight:1 }}>{days === 0 ? 'TODAY' : days === 1 ? '1 day' : `${days}d`}</div>
                    <div style={{ fontSize:12, color: T.textMute, marginTop:3 }}>{fmtDate(entry.decision_date)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {collisions.length > 0 && (
        <Section title="Earnings + FDA Collision Alerts" subtitle="Same-week earnings and FDA decisions — high volatility" accent={T.gold} isMobile={isMobile}>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {collisions.map((c,i) => (
              <div key={i} style={{ background:'linear-gradient(135deg,rgba(245,158,11,0.06),rgba(239,68,68,0.04))', border:'1px solid rgba(245,158,11,0.3)', borderLeft:'3px solid #f59e0b', borderRadius:10, padding:'16px 18px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <span style={{ fontWeight:800, color: T.emerald, fontSize:16 }}>{c.ticker}</span>
                  <span style={{ fontSize:11, fontWeight:700, color: T.gold, background:'rgba(251,191,36,0.1)', padding:'2px 8px', borderRadius:4 }}>COLLISION</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.18)', borderRadius:8, padding:'10px 14px' }}>
                    <div style={{ fontSize:10, fontWeight:700, color: T.blue, marginBottom:4, letterSpacing:'0.8px' }}>EARNINGS</div>
                    <div style={{ fontSize:14, color: T.text, fontWeight:600 }}>{fmtDate(c.earnings_date)}</div>
                  </div>
                  <div style={{ background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.18)', borderRadius:8, padding:'10px 14px' }}>
                    <div style={{ fontSize:10, fontWeight:700, color: T.red, marginBottom:4, letterSpacing:'0.8px' }}>FDA DECISION</div>
                    <div style={{ fontSize:14, color: T.text, fontWeight:600 }}>{c.fda_drug?.substring(0,30)}</div>
                    <div style={{ fontSize:12, color: T.textSub, marginTop:2 }}>{fmtDate(c.fda_date)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {recentTrades.length > 0 && (
        <Section title="Recent Insider Trades" subtitle="Last 7 days · SEC Form 4 filings" accent={T.gold} isMobile={isMobile} onViewAll={() => onTabChange?.('insider')}>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {recentTrades.map((t,i) => {
              const isBuy      = t.trade_type === 'BUY';
              const tradeColor = isBuy ? T.green : T.red;
              return (
                <div key={i} style={{ backgroundColor: T.surface, border:`1px solid ${T.border}`, borderLeft:`3px solid ${tradeColor}`, borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <span style={{ fontWeight:800, color: T.emerald, fontSize:15 }}>{t.ticker}</span>
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background: isBuy ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: tradeColor }}>{isBuy ? '▲ BUY' : '▼ SELL'}</span>
                      {t.has_fda_event && <span style={{ fontSize:10, background:'rgba(245,158,11,0.15)', color:'#f59e0b', borderRadius:4, padding:'1px 6px', fontWeight:700 }}>FDA</span>}
                    </div>
                    <div style={{ fontSize:13, color: T.textSub }}>{t.insider_name} · {t.insider_title?.substring(0,40)}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:16, fontWeight:700, color: tradeColor }}>{fmt$(t.total_value)}</div>
                    <div style={{ fontSize:12, color: T.textMute, marginTop:2 }}>{fmtDate(t.trade_date)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {newDrugs.length > 0 && (
        <Section title="New FDA Drugs Added" subtitle="Added to database this week" accent={T.blue} isMobile={isMobile} onViewAll={() => onTabChange?.('fda')}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap:8 }}>
            {newDrugs.map((drug,i) => {
              const riskColor = drug.risk_level === 'High' ? T.red : drug.risk_level === 'Low' ? T.green : T.amber;
              return (
                <div key={i} style={{ backgroundColor: T.surface, border:`1px solid ${T.border}`, borderLeft:`3px solid ${riskColor}`, borderRadius:10, padding:'14px 18px' }}>
                  <div style={{ fontWeight:700, color: T.text, fontSize:15, marginBottom:4 }}>{drug.drug_name}</div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, color: T.textSub }}>{drug.manufacturer?.substring(0,30)}</span>
                    <span style={{ fontSize:11, fontWeight:700, color: riskColor, background:`${riskColor}15`, padding:'3px 10px', borderRadius:5 }}>{drug.risk_level}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {newTrials.length > 0 && (
        <Section title="New Clinical Trials Added" subtitle="Added to database this week" accent={T.purple} isMobile={isMobile} onViewAll={() => onTabChange?.('trials')}>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {newTrials.map((trial,i) => (
              <div key={i} style={{ backgroundColor: T.surface, border:`1px solid ${T.border}`, borderLeft:`3px solid ${T.purple}`, borderRadius:10, padding:'14px 18px' }}>
                <div style={{ fontWeight:700, color: T.text, fontSize:15, marginBottom:4 }}>{trial.title?.substring(0,80)}{trial.title?.length > 80 ? '...' : ''}</div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:13, color: T.textSub }}>{trial.company?.substring(0,30)}</span>
                  <span style={{ fontSize:12, color: T.purple }}>{trial.phase?.replace(/[[\]']/g,'').trim()}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {!hasAnything && (
        <div style={{ textAlign:'center', padding:'80px 40px', backgroundColor: T.surface, border:`1px solid ${T.border}`, borderRadius:12 }}>
          <div style={{ fontSize:19, fontWeight:700, color: T.textSub, marginBottom:10 }}>All caught up</div>
          <div style={{ fontSize:15, color: T.textMute }}>No new data added in the last 7 days. Check back after the weekend scrapers run.</div>
        </div>
      )}

      <div style={{ marginTop:28, padding:'14px 18px', backgroundColor: T.surface, border:`1px solid ${T.border}`, borderRadius:10 }}>
        <p style={{ margin:0, fontSize:14, color: T.textMute, lineHeight:1.7 }}>Data refreshes weekly via automated scrapers. PDUFA dates are estimates — always verify with official FDA sources. Not financial advice.</p>
      </div>

      <style>{`
        @keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
        @keyframes pulse   { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.45;transform:scale(0.85);} }
      `}</style>
    </div>
  );
}

function Section({ title, subtitle, accent, children, isMobile, onViewAll }) {
  return (
    <div style={{ marginBottom:28 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
        <div>
          <h3 style={{ margin:0, fontSize: isMobile ? 17 : 20, fontWeight:800, color:'#f0f6ff', letterSpacing:'-0.3px' }}>{title}</h3>
          {subtitle && <div style={{ fontSize:13, color:'#4a6a8a', marginTop:3 }}>{subtitle}</div>}
        </div>
        {onViewAll && (
          <button onClick={onViewAll} style={{ backgroundColor:'transparent', border:`1px solid ${accent}40`, color: accent, padding:'6px 16px', borderRadius:7, cursor:'pointer', fontSize:13, fontWeight:600, transition:'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = `${accent}10`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = `${accent}40`; e.currentTarget.style.background = 'transparent'; }}>
            View all →
          </button>
        )}
      </div>
      <div style={{ borderLeft:`2px solid ${accent}30`, paddingLeft:14 }}>{children}</div>
    </div>
  );
}